<template>
  <Teleport :to="to">
    <div
      v-show="isVisible"
      ref="chartPopover"
      :style="popoverStyle"
      class="chart-popover"
      :class="{
        'pointer-events-none': !triggerIsClick,
        'hide-arrow': shouldHideArrow,
        [customClass]: true,
      }"
      :data-edge-right="noSpaceOnRight"
      :data-edge-left="noSpaceOnLeft"
    >
      <div class="info d-flex align-items-center no-wrap justify-content-between mb-10 w-full">
        <slot
          name="popover-header"
          :column-data="columnData"
        />
        <div class="d-flex justify-content-between">
          <div
            v-if="columnData.title"
            class="main ellipsis w-full"
          >
            {{ columnData.title }}
          </div>
          <button
            v-if="triggerIsClick"
            class="close d-flex align-items-center ml-8 cursor-pointer"
            type="button"
            aria-hidden="true"
            @click="close"
          >
            <i class="hi hi-close hi-10" />
          </button>
        </div>
      </div>
      <slot name="popover-content" />
    </div>
  </Teleport>
</template>

<script>
import Teleport from 'vue2-teleport';

const POPOVER_ARROW_SIZE = { height: 10, width: 20 };

export default {
  name: 'AppChartPopover',
  components: { Teleport },
  props: {
    to: {
      type: String,
      default: 'body',
    },
    columnData: {
      type: Object,
      required: true,
    },
    position: {
      type: Object,
      required: true,
    },
    trigger: {
      type: String,
      default: 'mouseover',
      validator: (value) => ['mouseover', 'click'].includes(value),
    },
    customClass: {
      type: String,
      default: '',
    },
    /**
     * Positioning strategy for the popover:
     * - 'window': Position relative to viewport bounds (default behavior)
     * - 'container': Position relative to the target container bounds specified by 'to' prop
     *
     * Use 'container' when teleporting to a specific scrollable container to keep
     * the popover within that container's boundaries instead of the viewport.
     */
    strategy: {
      type: String,
      default: 'window',
      validator: (value) => ['window', 'container'].includes(value),
    },
    stickyColumnWidth: {
      type: Number,
      default: 0,
    },
    /**
     * Space to reserve at the top of the viewport for sticky headers.
     * When the available space above the element is less than this value,
     * the popover will prefer bottom placement to avoid being hidden under sticky headers.
     */
    topReservedSpace: {
      type: Number,
      default: 0,
    },
    arrowSize: {
      type: Number,
      default: POPOVER_ARROW_SIZE.height,
    },
  },
  data() {
    return {
      raf: null,
      chartPopoverHeight: 0,
      chartPopoverWidth: 0,
      scrollY: 0,
      innerWidth: 0,
      containerBounds: null,
      resizeObserver: null,
      repositionTimeout: null,
    };
  },
  computed: {
    arrowHeightWithMargin() {
      return this.arrowSize + 3;
    },
    isVisible() {
      return Object.keys(this.columnData).length > 0;
    },
    popoverWidth() {
      return this.chartPopoverWidth || 200;
    },
    isContainerStrategy() {
      return this.strategy === 'container';
    },
    availableWidth() {
      if (this.isContainerStrategy) {
        return this.containerBounds?.width || 0;
      }

      return this.innerWidth || window.innerWidth;
    },
    halfPopoverWidth() {
      return this.popoverWidth / 2;
    },
    noSpaceOnTop() {
      if (this.isContainerStrategy && this.containerBounds) {
        // For container strategy, use only container-relative positions
        // position.y and position.bottomY are already relative to the container
        const elementTopInContainer = this.position.y;
        const elementBottomInContainer = this.position.bottomY;
        // Calculate space within container bounds only, ignoring window scroll
        const spaceAboveInContainer = elementTopInContainer - this.topReservedSpace;
        const spaceBelowInContainer = this.containerBounds.height - elementBottomInContainer;
        const requiredSpace = this.chartPopoverHeight + this.arrowHeightWithMargin;
        const fitsAbove = spaceAboveInContainer >= requiredSpace;
        const fitsBelow = spaceBelowInContainer >= requiredSpace;
        let result;

        if (fitsAbove) {
          result = false;
        } else if (fitsBelow) {
          result = true;
        } else {
          result = spaceBelowInContainer > spaceAboveInContainer;
        }

        return result;
      }

      // Window strategy: uses different logic since viewport can scroll
      const spaceAbove = this.position.y - this.scrollY - this.topReservedSpace;
      const viewportBottom = window.innerHeight + this.scrollY;
      const spaceBelow = viewportBottom - this.position.bottomY;
      // Position below if:
      // 1. Not enough space above (including reserved space)
      // 2. More space below
      // 3. Element is too close to top reserved area
      const tooCloseToTop =
        this.position.y - this.scrollY < this.topReservedSpace + this.chartPopoverHeight + this.arrowHeightWithMargin;
      const result =
        spaceAbove < this.chartPopoverHeight + this.arrowHeightWithMargin || spaceBelow > spaceAbove || tooCloseToTop;

      return result;
    },
    noSpaceOnRight() {
      // IMPORTANT: When using container strategy with a scrollable container:
      // - this.position.x is the ABSOLUTE position within the scrollable content
      // - We need to check if the popover fits within BOTH:
      //   1. The VISIBLE viewport (for immediate display)
      //   2. The total scrollable content width (to prevent expanding the container)
      if (this.isContainerStrategy && this.containerBounds?.hasOverflow) {
        const visibleRightEdge = this.containerBounds.scrollLeft + this.containerBounds.width;
        const popoverRightEdge = this.position.x + this.halfPopoverWidth;
        // IMPORTANT: Use containerBounds.scrollWidth if available, as it represents
        // the original content width before any popover-induced expansion
        const originalScrollWidth = this.containerBounds.scrollWidth || this.containerBounds.width;
        // Check against both visible edge AND original content width
        const result = popoverRightEdge > visibleRightEdge || popoverRightEdge > originalScrollWidth;

        return result;
      }

      // Standard check for non-scrollable containers or window strategy
      const result = this.position.x + this.halfPopoverWidth > this.availableWidth;

      return result;
    },
    noSpaceOnLeft() {
      // IMPORTANT: When using container strategy with a scrollable container:
      // - Check if popover left edge would be before the visible viewport
      if (this.isContainerStrategy && this.containerBounds?.hasOverflow) {
        const visibleLeftEdge = this.containerBounds.scrollLeft + this.stickyColumnWidth;
        const popoverLeftEdge = this.position.x - this.halfPopoverWidth;
        const result = popoverLeftEdge < visibleLeftEdge;

        return result;
      }

      // Standard check - account for sticky column width
      const result = this.position.x - this.halfPopoverWidth < this.stickyColumnWidth;

      return result;
    },
    xPosition() {
      if (!this.availableWidth) {
        return this.position.x || 0;
      }

      let finalX = this.position.x;

      // IMPORTANT: For scrollable containers, we need to adjust based on visible viewport
      if (this.isContainerStrategy && this.containerBounds?.hasOverflow) {
        if (this.noSpaceOnRight) {
          // When using translateX(-100%), left position should be at the right edge
          const visibleRightEdge = this.containerBounds.scrollLeft + this.containerBounds.width;

          finalX = visibleRightEdge;
        } else if (this.noSpaceOnLeft) {
          // When using translateX(0), left position is the actual left edge plus sticky column width
          finalX = this.containerBounds.scrollLeft + this.stickyColumnWidth;
        }
      } else {
        // Standard positioning for non-scrollable containers
        if (this.noSpaceOnRight) {
          finalX = this.availableWidth - this.halfPopoverWidth;
        } else if (this.noSpaceOnLeft) {
          // When using translateX(0), position at sticky column edge
          finalX = this.stickyColumnWidth > 0 ? this.stickyColumnWidth : this.halfPopoverWidth;
        }
      }

      return finalX;
    },
    yPosition() {
      const result = this.noSpaceOnTop
        ? this.position.bottomY + this.arrowHeightWithMargin
        : this.position.y - this.chartPopoverHeight - this.arrowHeightWithMargin;

      return result;
    },
    shouldHideArrow() {
      // Hide arrow when at edge with custom transform to prevent container expansion
      if (this.isContainerStrategy && this.containerBounds?.hasOverflow) {
        if (this.noSpaceOnRight || this.noSpaceOnLeft) {
          // Check if the arrow would extend beyond container bounds
          const arrowOffset = this.position.x - this.xPosition;
          const arrowExtendsRight = this.noSpaceOnRight && Math.abs(arrowOffset) < 20;
          const arrowExtendsLeft = this.noSpaceOnLeft && Math.abs(arrowOffset) < 20;

          return arrowExtendsRight || arrowExtendsLeft;
        }
      }

      return false;
    },
    arrowPositionX() {
      let result = '50%';

      // When overriding transform, adjust arrow position accordingly
      if (this.noSpaceOnRight && this.isContainerStrategy && this.containerBounds?.hasOverflow) {
        // With translateX(-100%), arrow needs to be positioned from the right
        const visibleRightEdge = this.containerBounds.scrollLeft + this.containerBounds.width;
        const offset = this.position.x - visibleRightEdge;

        result = `calc(100% + ${offset}px)`;
      } else if (this.noSpaceOnLeft && this.isContainerStrategy && this.containerBounds?.hasOverflow) {
        // With translateX(0), arrow position is from the left
        // Calculate offset from the adjusted popover position, not from scrollLeft
        const popoverLeft = this.containerBounds.scrollLeft + this.stickyColumnWidth;
        const offset = this.position.x - popoverLeft;

        result = `${offset}px`;
      } else if (this.noSpaceOnRight) {
        // Standard arrow positioning for translateX(-50%)
        const offset = this.position.x + this.halfPopoverWidth - this.availableWidth;

        result = `calc(50% + ${offset}px)`;
      } else if (this.noSpaceOnLeft) {
        if (this.stickyColumnWidth > 0) {
          // With translateX(0), arrow position is from the left
          const popoverLeft = this.stickyColumnWidth;
          const offset = this.position.x - popoverLeft;

          result = `${offset}px`;
        } else {
          // Standard case without sticky column
          const offset = this.position.x - this.halfPopoverWidth;

          result = `calc(50% - ${offset}px)`;
        }
      }

      return result;
    },
    popoverStyle() {
      // For edge cases, we need to override the default translateX(-50%)
      let transform = '';

      if (this.noSpaceOnRight && this.isContainerStrategy && this.containerBounds?.hasOverflow) {
        // Position popover with its right edge at the container edge
        transform = 'translateX(-100%)';
      } else if (
        this.noSpaceOnLeft &&
        ((this.isContainerStrategy && this.containerBounds?.hasOverflow) || this.stickyColumnWidth > 0)
      ) {
        // Position popover with its left edge at the position
        // This applies to scrollable containers and standard positioning with sticky columns
        transform = 'translateX(0)';
      }

      const style = {
        left: `${this.xPosition}px`,
        top: `${this.yPosition}px`,
        '--report-arrow-positionX': this.arrowPositionX,
        '--report-arrow-positionY': this.noSpaceOnTop ? '100%' : `-${this.arrowSize}px`,
        '--report-arrow-rotate': this.noSpaceOnTop ? '180deg' : '0deg',
      };

      if (transform) {
        style.transform = transform;
      }

      return style;
    },
    triggerIsClick() {
      return this.trigger === 'click';
    },
  },
  watch: {
    columnData: {
      handler() {
        this.updateDimensions();
      },
      deep: true,
      immediate: true,
    },
    position: {
      handler() {
        this.updateDimensions();
      },
      deep: true,
    },
    strategy: {
      handler() {
        this.setupEventListeners();
        this.updateDimensions();
      },
      immediate: true,
    },
    to: {
      handler() {
        this.setupEventListeners();
        this.updateDimensions();
      },
    },
    isVisible: {
      handler(newValue) {
        if (newValue) {
          this.setupResizeObserver();
          this.debouncedReposition();
        } else {
          this.cleanupResizeObserver();
        }
      },
    },
  },
  mounted() {
    this.setupEventListeners();
    this.updateDimensions();
  },
  beforeDestroy() {
    cancelAnimationFrame(this.raf);
    this.raf = null;
    clearTimeout(this.repositionTimeout);
    this.repositionTimeout = null;
    this.cleanupEventListeners();
    this.cleanupResizeObserver();
  },
  methods: {
    getTargetContainer() {
      if (this.to === 'body') {
        return document.body;
      }

      return document.querySelector(this.to);
    },
    getContainerBounds() {
      const container = this.getTargetContainer();

      if (!container) {
        return null;
      }

      const containerRect = container.getBoundingClientRect();
      const hasOverflow =
        container.scrollHeight > container.clientHeight || container.scrollWidth > container.clientWidth;
      const bounds = {
        width: hasOverflow ? container.clientWidth : containerRect.width,
        height: hasOverflow ? container.clientHeight : containerRect.height,
        left: containerRect.left,
        top: containerRect.top,
        right: containerRect.right,
        bottom: containerRect.bottom,
        scrollLeft: container.scrollLeft || 0,
        scrollTop: container.scrollTop || 0,
        scrollWidth: container.scrollWidth || containerRect.width,
        scrollHeight: container.scrollHeight || containerRect.height,
        hasOverflow,
      };

      return bounds;
    },
    updateDimensions() {
      this.raf = requestAnimationFrame(() => {
        this.scrollY = window.scrollY;
        this.innerWidth = window.innerWidth;

        if (this.strategy === 'container') {
          this.containerBounds = this.getContainerBounds();
        }

        const popover = this.$refs.chartPopover;

        this.chartPopoverWidth = popover?.clientWidth || 0;
        this.chartPopoverHeight = popover?.clientHeight || 0;
      });
    },
    setupEventListeners() {
      this.cleanupEventListeners();

      window.addEventListener('resize', this.updateDimensions);

      if (this.strategy === 'container') {
        const container = this.getTargetContainer();

        if (container && container !== document.body) {
          // DISABLED: Repositioning on scroll can cause container expansion issues
          // Since the popover is inside the scrollable container, it moves naturally with scroll
          // container.addEventListener('scroll', this.updateDimensions);
          // this._containerScrollListener = { container, handler: this.updateDimensions };
        }
      } else {
        window.addEventListener('scroll', this.updateDimensions);
        this._windowScrollListener = true;
      }
    },
    cleanupEventListeners() {
      window.removeEventListener('resize', this.updateDimensions);

      if (this._windowScrollListener) {
        window.removeEventListener('scroll', this.updateDimensions);
        this._windowScrollListener = false;
      }

      // DISABLED: Container scroll listener disabled to prevent expansion issues
      // if (this._containerScrollListener) {
      //   const { container, handler } = this._containerScrollListener;
      //   container.removeEventListener('scroll', handler);
      //   this._containerScrollListener = null;
      // }
    },
    close() {
      this.$emit('close');
    },
    reposition() {
      this.updateDimensions();
    },
    debouncedReposition() {
      if (this.repositionTimeout) {
        clearTimeout(this.repositionTimeout);
      }

      // Skip immediate update if we're at the edge to prevent feedback loops
      const isAtEdge = this.noSpaceOnRight || this.noSpaceOnLeft;

      if (!isAtEdge) {
        // Immediate repositioning for fast DOM updates
        this.$nextTick(() => this.updateDimensions());
      }

      // Debounced repositioning for async content loading
      this.repositionTimeout = setTimeout(() => {
        this.updateDimensions();
      }, 100);
    },
    setupResizeObserver() {
      this.cleanupResizeObserver();

      if (this.$refs.chartPopover && window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => {
          this.debouncedReposition();
        });

        this.resizeObserver.observe(this.$refs.chartPopover);
      } else {
        // Fallback: periodically check for size changes if ResizeObserver not supported
        this.resizeObserver = setInterval(() => {
          this.updateDimensions();
        }, 200);
      }
    },
    cleanupResizeObserver() {
      if (this.resizeObserver) {
        if (this.resizeObserver.disconnect) {
          // ResizeObserver
          this.resizeObserver.disconnect();
        } else {
          // setInterval fallback
          clearInterval(this.resizeObserver);
        }
        this.resizeObserver = null;
      }
    },
  },
};
</script>
